import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { BrandLogo, BRAND_LOGO_MAX_HEIGHT } from './BrandLogo';
import { APP_NAME } from '../../constants/brand';
import { superAdminAPI } from '../../services/api';

export const PLATFORM_BRAND_UPDATED_EVENT = 'platform-brand-updated';

interface TenantBrandLogoProps {
  collapsed?: boolean;
  height?: number;
  isSuperAdmin?: boolean;
}

const superAdminBadgeSx = {
  color: 'rgba(255,255,255,0.8)',
  fontSize: 10,
  letterSpacing: 1.6,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  textAlign: 'center' as const,
  width: '100%',
  mt: 0.75,
};

const superAdminLogoSx = {
  maxHeight: BRAND_LOGO_MAX_HEIGHT,
  height: 'auto',
  objectFit: 'contain' as const,
  mx: 'auto',
  borderRadius: '6px',
};

function SuperAdminBrandBlock({
  collapsed,
  logoUrl,
  height,
}: {
  collapsed: boolean;
  logoUrl: string;
  height: number;
}) {
  if (collapsed) {
    if (logoUrl) {
      return (
        <Box
          component="img"
          src={logoUrl}
          alt={APP_NAME}
          sx={{ ...superAdminLogoSx, maxHeight: height, maxWidth: 64 }}
        />
      );
    }
    return (
      <Box sx={{ width: '100%', maxWidth: 64, mx: 'auto' }}>
        <BrandLogo height={height} fullWidth />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      {logoUrl ? (
        <Box component="img" src={logoUrl} alt={`${APP_NAME} — Super Admin`} sx={superAdminLogoSx} />
      ) : (
        <BrandLogo height={height} fullWidth alt={`${APP_NAME} — Super Admin`} />
      )}
      <Typography variant="caption" sx={superAdminBadgeSx}>
        Super Admin
      </Typography>
    </Box>
  );
}

/** Sidebar logo — tenant upload, super-admin platform logo, or Tripiigo default */
export const TenantBrandLogo: React.FC<TenantBrandLogoProps> = ({
  collapsed = false,
  height = 36,
  isSuperAdmin = false,
}) => {
  const { user } = useAuth();
  const [platformLogo, setPlatformLogo] = useState('');
  const tenantLogo = user?.company?.companyLogo;
  const logoUrl = isSuperAdmin ? (platformLogo || '') : tenantLogo;

  const loadPlatformLogo = useCallback(() => {
    if (!isSuperAdmin) return;
    superAdminAPI.getBillingProfile()
      .then((res) => {
        if (res.data?.logo) setPlatformLogo(res.data.logo);
        else setPlatformLogo('');
      })
      .catch(() => { /* fallback to Tripiigo brand */ });
  }, [isSuperAdmin]);

  useEffect(() => {
    loadPlatformLogo();
  }, [loadPlatformLogo]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const onBrandUpdated = () => loadPlatformLogo();
    window.addEventListener(PLATFORM_BRAND_UPDATED_EVENT, onBrandUpdated);
    return () => window.removeEventListener(PLATFORM_BRAND_UPDATED_EVENT, onBrandUpdated);
  }, [isSuperAdmin, loadPlatformLogo]);

  if (isSuperAdmin) {
    return (
      <SuperAdminBrandBlock
        collapsed={collapsed}
        logoUrl={logoUrl || ''}
        height={collapsed ? height : height}
      />
    );
  }

  if (collapsed) {
    if (logoUrl) {
      return (
        <Box
          component="img"
          src={logoUrl}
          alt={user?.company?.companyName || 'Logo'}
          sx={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 0 }}
        />
      );
    }
    return <BrandLogo height={36} />;
  }

  if (logoUrl) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={user?.company?.companyName || 'Logo'}
        sx={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
          maxHeight: height,
          objectFit: 'contain',
          objectPosition: 'left center',
          borderRadius: 0,
        }}
      />
    );
  }

  return <BrandLogo height={height} fullWidth />;
};
